import * as THREE from 'three';
import { BASE_ATLAS, MINERAL_ART, TOOL_ART, CREATURE_ART, CREATURE_ATLAS, type AtlasDef } from './artAtlas';
import type { MineralKind, CreatureKind, ChipId } from './config';

const textures = new Map<string, THREE.Texture>();
const sources = new Map<string, THREE.Texture>();
function spriteFromAtlas(atlas: AtlasDef, index: number, size: number): THREE.Sprite {
  const key = `${atlas.file}:${index}`;
  let texture = textures.get(key);
  if (!texture) {
    let source = sources.get(atlas.file);
    if (!source) {
      source = new THREE.TextureLoader().load(atlas.file, () => {
        for (const [key, variant] of textures) if (key.startsWith(atlas.file + ':')) variant.needsUpdate = true;
      }, undefined, () => { const failed = sources.get(atlas.file); if (failed) failed.userData.loadError = true; });
      sources.set(atlas.file, source);
    }
    // Share the decoded image, but upload each cropped texture only once it exists.
    // Texture.clone() marks an unloaded source for upload and warns on every frame.
    texture = new THREE.Texture();
    texture.source = source.source;
    if (source.image) texture.needsUpdate = true;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    const [x,y,width,height] = atlas.regions[index];
    texture.repeat.set(width / atlas.width, height / atlas.height);
    texture.offset.set(x / atlas.width, 1 - (y + height) / atlas.height);
    textures.set(key, texture);
  }
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthWrite: false }));
  const region = atlas.regions[index];
  sprite.scale.set(size, size * region[3] / region[2], 1);
  return sprite;
}
export function createPixelSprite(index: number, size: number): THREE.Sprite { return spriteFromAtlas(BASE_ATLAS,index,size); }
export function createMineralSprite(kind: MineralKind, size: number): THREE.Sprite {
  const [atlas,index] = MINERAL_ART[kind]; return spriteFromAtlas(atlas,index,size);
}

export function createCreatureSprite(kind:CreatureKind,size:number):THREE.Sprite {return spriteFromAtlas(CREATURE_ATLAS,CREATURE_ART[kind],size);}
export function createToolSprite(id:ChipId,size:number):THREE.Sprite {const [atlas,index]=TOOL_ART[id];return spriteFromAtlas(atlas,index,size);}
export function createAtlasSprite(atlas:AtlasDef,index:number,size:number):THREE.Sprite {return spriteFromAtlas(atlas,index,size);}

export function pixelAssetsStatus(): 'ready' | 'loading' | 'error' {
  for (const source of sources.values()) if (source.userData.loadError) return 'error';
  for (const source of sources.values()) if (!(source.image as HTMLImageElement | undefined)?.complete) return 'loading';
  return 'ready';
}

export function disposePixelAssets(): void {
  for (const texture of textures.values()) texture.dispose();
  for (const texture of sources.values()) texture.dispose();
  textures.clear(); sources.clear();
}
